/**
 * Utilities for persisting checkout context across authentication flows
 *
 * When users select a plan but need to login first, we persist their selection
 * so they can resume checkout after authentication completes.
 */

const CHECKOUT_CONTEXT_KEY = 'neokanban_pending_checkout';

export interface CheckoutContext {
  planId: string;
  billingPeriod: 'monthly' | 'yearly';
  timestamp: number;
  returnPath?: string;
}

/**
 * Save checkout context to sessionStorage
 * Automatically expires after 30 minutes
 */
export function saveCheckoutContext(context: Omit<CheckoutContext, 'timestamp'>): void {
  const contextWithTimestamp: CheckoutContext = {
    ...context,
    timestamp: Date.now(),
  };

  try {
    sessionStorage.setItem(CHECKOUT_CONTEXT_KEY, JSON.stringify(contextWithTimestamp));
  } catch (error) {
    console.error('Failed to save checkout context:', error);
  }
}

/**
 * Retrieve checkout context from sessionStorage
 * Returns null if expired (>30 minutes old) or invalid
 */
export function getCheckoutContext(): CheckoutContext | null {
  try {
    const stored = sessionStorage.getItem(CHECKOUT_CONTEXT_KEY);
    if (!stored) {
      return null;
    }

    const context = JSON.parse(stored) as CheckoutContext;

    // Check if expired (30 minutes)
    const MAX_AGE_MS = 30 * 60 * 1000;
    const age = Date.now() - context.timestamp;

    if (age > MAX_AGE_MS) {
      clearCheckoutContext();
      return null;
    }

    return context;
  } catch (error) {
    console.error('Failed to retrieve checkout context:', error);
    return null;
  }
}

/**
 * Clear checkout context from sessionStorage
 * Call this after successful checkout or explicit cancellation
 */
export function clearCheckoutContext(): void {
  try {
    sessionStorage.removeItem(CHECKOUT_CONTEXT_KEY);
  } catch (error) {
    console.error('Failed to clear checkout context:', error);
  }
}

/**
 * Check if there's a pending checkout context
 */
export function hasPendingCheckout(): boolean {
  return getCheckoutContext() !== null;
}
